import type { RequiredGeneric } from '@pba/react-utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Form } from 'ui';
import { useUser } from 'features/auth';
import { InvitationSearchInput } from '../../invitation-search-input';
import { InvitationsList } from '../../invitations-list';
import { useInvitationSearchOptions, useOnInvatiotionSelect, useReminderInvitationCount } from '../hooks';
import type { InvitationSearchOption } from '../types';

export type ExtendedReminderInvitationsFormValues = {
  invitations?: InvitationSearchOption[];
};

type P = {
  alreadyInvitedCount?: number;
  isRequired?: boolean;
  label?: string;
};

const ReminderInvitationsFormItem = <T extends ExtendedReminderInvitationsFormValues = never>({
  alreadyInvitedCount = 0,
  isRequired = false,
  label,
}: RequiredGeneric<T, P>) => {
  const { isEventsAdmin } = usePermissions();
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const {
    nodeUserData: { _id },
  } = useUser();
  const { isLoading, options } = useInvitationSearchOptions({
    search,
  });
  const invitations = Form.useWatch<InvitationSearchOption[] | undefined>('invitations') ?? [];
  const { invitationCount, invitationLimit } = useReminderInvitationCount({ alreadyInvitedCount, invitations });

  const selectedValues = new Set(invitations.map((invitation) => invitation.value));
  const availableOptions = options.filter((option) => !selectedValues.has(option.value) && option.value !== _id);
  const isLimitReached = invitationCount >= invitationLimit;
  const handleSelect = useOnInvatiotionSelect({ invitationCount, invitationLimit, invitations });

  return (
    <>
      <Form.Item<ExtendedReminderInvitationsFormValues>
        className="reminder-invitations-form-item__search-input"
        extra={
          isEventsAdmin({})
            ? t('Type user name or community you want to remind. You can remind up to {{count}} people.', {
                count: invitationLimit,
              })
            : t('Type user name you want to remind. You can remind up to {{count}} people.', {
                count: invitationLimit,
              })
        }
        label={label ?? 'Add people'}
        name="invitations"
        rules={[{ required: isRequired }]}
      >
        <InvitationSearchInput
          disabled={isLimitReached}
          loading={isLoading}
          onSearchChange={setSearch}
          onSelect={handleSelect}
          options={availableOptions}
          search={search}
        />
      </Form.Item>
      <InvitationsList invitations={invitations} showRoleSelect={false} />
    </>
  );
};

export default ReminderInvitationsFormItem;
